import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'antd';
import { InputNumber } from 'antd';

import { FormItem, IFormInstance } from '@/components';
import { Input } from '@/ui';
import { CheckmarkIcon } from '@/icons';
import {
  StyledButton,
  StyledForm,
  StyledTitle,
  StyledWrapper,
  FormColumn,
} from '../../components/CreateFilterValueForm/CreateFilterValueFormStyles';

import { SupportedLocales } from '@/models/localization.model';
import { IBlogCategorySaveDTO } from '@/models/blogCategories.model';

import useGetLocales from '@/modules/Locales/hooks/useGetLocales';

import { useGetRequiredRule } from '@/hooks';

type TBlogCategoryForm = {
  form: IFormInstance;
  modeKey: string;
  isEdit: boolean;
  initialValues: Partial<IBlogCategorySaveDTO> & { locale?: Record<string, { name?: string }> };
  onSubmit: (values: IBlogCategorySaveDTO) => Promise<void>;
  submitLoading: boolean;
};

const BlogCategoryForm = ({ form, modeKey, isEdit, initialValues, onSubmit, submitLoading }: TBlogCategoryForm) => {
  const { t } = useTranslation(['categories', 'common', 'validations']);
  const localesFromApi = useGetLocales();
  const locales = useMemo(
    () => (localesFromApi?.length ? localesFromApi : [SupportedLocales.UK, SupportedLocales.RU]),
    [localesFromApi],
  );
  const [activeTab, setActiveTab] = useState<string>(locales[0]);
  useEffect(() => {
    setActiveTab(locales[0]);
  }, [modeKey, locales]);
  const requiredRule = useGetRequiredRule({ t });
  const placeholder = t('common_placeholder_input', { ns: 'common' });

  const handleFinish = async (values: IBlogCategorySaveDTO) => {
    await onSubmit({
      position: Number(values.position),
      locale: values.locale,
    });
  };

  return (
    <StyledWrapper>
      <StyledTitle>{t(isEdit ? 'blog_category_form_edit_title' : 'blog_category_form_create_title')}</StyledTitle>
      <StyledForm form={form} onFinish={handleFinish} initialValues={initialValues} layout="vertical">
        <FormColumn>
          <FormItem name="position" label={t('blog_category_form_position')} rules={[requiredRule]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </FormItem>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={locales.map((locale) => ({
              key: locale,
              label: locale.toUpperCase(),
              forceRender: true,
              children: (
                <>
                  <FormItem
                    name={['locale', locale, 'name']}
                    label={t('blog_category_form_name')}
                    rules={[requiredRule]}
                  >
                    <Input placeholder={placeholder} />
                  </FormItem>
                </>
              ),
            }))}
          />
        </FormColumn>

        <StyledButton htmlType="submit" loading={submitLoading}>
          <CheckmarkIcon /> {t(isEdit ? 'blog_category_form_save' : 'blog_category_form_create')}
        </StyledButton>
      </StyledForm>
    </StyledWrapper>
  );
};

export default BlogCategoryForm;
